/**
 * Error taxonomy.
 *
 * Everything in `src/errors/` stays free of Vue, Pinia, vue-i18n and router imports. That rule is
 * what lets `main.ts` report a failure before Pinia exists, keeps this layer unit-testable without a
 * mounted app, and prevents a crash inside the crash handler. Presentation and translation belong to
 * `src/stores/error.ts`; this layer only classifies.
 */

/** What went wrong, at the granularity the UI and Sentry care about. */
// eslint-disable-next-line no-shadow
export enum ErrorKind {
  NETWORK = 'network',
  TIMEOUT = 'timeout',
  AUTH_EXPIRED = 'auth-expired',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not-found',
  VALIDATION = 'validation',
  CONFLICT = 'conflict',
  RATE_LIMITED = 'rate-limited',
  SERVER = 'server',
  CHUNK_LOAD = 'chunk-load',
  RENDER = 'render',
  ROUTE = 'route',
  UNHANDLED_REJECTION = 'unhandled-rejection',
  EXPECTED = 'expected',
  UNKNOWN = 'unknown'
}

/** How loudly to present it. */
// eslint-disable-next-line no-shadow
export enum ErrorTier {
  /** Nothing shown — something else is already handling it (a 401 is mid-redirect). */
  SILENT = 'silent',
  TOAST = 'toast',
  /** The caller renders `<ErrorState>` in place of the panel that failed. */
  INLINE = 'inline',
  /** The whole view is unusable; `<ErrorScreen>` replaces the router outlet. */
  FATAL = 'fatal'
}

/** Where the error entered the app. Useful on its own when triaging in Sentry. */
// eslint-disable-next-line no-shadow
export enum ErrorSource {
  HTTP = 'http',
  RENDER = 'render',
  ROUTER = 'router',
  GLOBAL = 'global',
  REJECTION = 'rejection',
  BOOTSTRAP = 'bootstrap',
  MANUAL = 'manual'
}

/**
 * An error we deliberately show the user and deliberately keep out of Sentry.
 *
 * Several places fabricate `{ name, message }` literals purely to reuse `setError` as a toast, which
 * sends synthetic non-errors to Sentry. Throw this instead.
 */
export class ExpectedError extends Error {
  constructor(message: string, name: string = 'ExpectedError') {
    super(message);
    this.name = name;
  }
}

/** Request details worth keeping. Paths and params are already redacted when this is built. */
export interface NormalizedRequest {
  method?: string;
  path?: string;
  params?: Record<string, string>;
  bodyKeys?: string[];
  validationFields?: string[];
  requestId?: string;
  sentryTrace?: string;
  durationMs?: number;
}

/**
 * The single shape every error path converges on.
 *
 * `titleKey`/`messageKey` are i18n keys rather than strings because this layer cannot translate —
 * `src/stores/error.ts` resolves them.
 */
export interface NormalizedError {
  /** Shown to the user and tagged on the Sentry event, so support can find one from the other. */
  reference: string;
  kind: ErrorKind;
  tier: ErrorTier;
  source: ErrorSource;
  titleKey: string;
  messageKey: string;
  /** Server-authored copy, guarded and capped. Preferred over `messageKey` when present. */
  serverMessage?: string;
  httpStatus?: number;
  code?: string;
  request?: NormalizedRequest;
  /** Gates both the Sentry capture and the "Report a problem" affordance. */
  isReportable: boolean;
  isRetryable: boolean;
  /** Dedupe key. Excludes `reference` and timestamps so repeats of the same failure collapse. */
  fingerprint: string;
  /** The original thrown value. In-memory only — never serialized wholesale. */
  cause: unknown;
  occurredAt: string;
  sentryEventId?: string;
  /**
   * The caller's title for a refusal, read in place of `titleKey` when the server's reason is shown,
   * so the title names what was refused ("Can't delete group") rather than the kind ("Already changed").
   */
  refusalTitleKey?: string;
}

/** Extra classification hints from the call site. */
export interface NormalizeContext {
  source?: ErrorSource;
  tier?: ErrorTier;
  kind?: ErrorKind;
  /** See `NormalizedError.refusalTitleKey`. */
  refusalTitleKey?: string;
}

/** Non-enumerable slot used to carry a `NormalizedError` on the thrown value itself. */
export const ATTACHED_ERROR_KEY = '__appError';
